import { Api, Fixtuur } from '@fixtuur/backpack';
import * as O from 'fp-ts/Option';
import * as TE from 'fp-ts/TaskEither';
import { pipe } from 'fp-ts/lib/function';
import type { Dispatch } from 'redux';
import { RootState } from '../../store';
import * as Options from '../options';
import * as Cache from '../options/cache';
import * as Selectors from '../selectors';

const filterOptionsToConfigurator = (
    options: Fixtuur.Configuration.Options.Options,
    configurator: Api.ConfiguratorDefinition.ConfiguratorDefinition,
): Fixtuur.Configuration.Options.Options => {
    const propertyWhitelist = Object.values(configurator.partsMap)
        .flatMap(part => part.properties.concat(part.productProperty))
        .concat(Object.keys(configurator.configurableProperties));
    return Fixtuur.Configuration.Options.filterToNames(propertyWhitelist, options);
};

export function getCompatibleOptions() {
    return function getCompatibleOptionsThunk(dispatch: Dispatch, getState: () => RootState) {
        const state = getState();

        const buildSelections = Selectors.Selections.buildPropertySelections(state);
        const productSelectionsSolo = Selectors.Selections.productSelections(state);
        const rootProductLockSelections = Selectors.Selections.groupingCodeSelections(state);

        const serviceConfig = Selectors.Core.serviceConfig(state);
        const currentConfigurator = Selectors.Initialisation.currentConfiguratorDefinition(state);

        // Our product selections need to include all of our selections in order to function properly.
        // If we only include the product selections, they won't take into account the build properties that
        // are currently selected, and if they don't include the root product lock selections, then we
        // might allow moving away from the root product - which would kinda defeat the point of the root product lock.
        const productSelections = Fixtuur.Configuration.Selections.concatNullable(
            productSelectionsSolo,
            Fixtuur.Configuration.Selections.concatNullable(
                rootProductLockSelections,
                buildSelections,
            ),
        );

        /**
         * Property Selections need to include Root Product Lock Selections to make sure we get data for the given SKU family.
         */
        const propertySelections = Fixtuur.Configuration.Selections.concatNullable(
            rootProductLockSelections,
            buildSelections,
        );

        const buildPending = Selectors.Options.pendingBuildOptions(state);
        const productPending = Selectors.Options.pendingProductOptions(state);

        if (
            serviceConfig &&
            currentConfigurator &&
            propertySelections !== null &&
            productSelections !== null
        ) {
            // we need to handle a request if this thunk gets called while there's no build pending - in which case we set a build pending and make the call / fetch from cache
            // however, we shouldn't make a call if there's already a build pending, and the selections are the same as the pending build selections - or we'll get duplicate calls!
            if (
                buildPending === null ||
                !Fixtuur.Configuration.Selections.equals(propertySelections, buildPending)
            ) {
                const cached = Cache.get(propertySelections, state.options.cache);

                if (O.isSome(cached)) {
                    pipe(
                        cached,
                        O.map(options => {
                            dispatch(Options.setBuildOptions(options));
                            dispatch(Options.setPendingBuildOptions(null));
                        }),
                    );
                } else {
                    dispatch(Options.setPendingBuildOptions(propertySelections));

                    pipe(
                        Api.Configuration.Options.fromConfigurationSpace(
                            serviceConfig,
                            currentConfigurator.configurationSpaceId,
                            pipe(propertySelections, Fixtuur.Configuration.Selections.toApi),
                        ),
                        TE.mapLeft(error => {
                            dispatch(Options.setPendingBuildOptions(null));
                            throw error;
                        }),
                        TE.map(x => Fixtuur.Configuration.Options.fromApi(x)),
                        TE.map(options =>
                            filterOptionsToConfigurator(options, currentConfigurator),
                        ),
                        TE.map(options => {
                            dispatch(Options.setBuildOptions(options));
                            dispatch(Options.setCachedOption({ key: propertySelections, options }));
                            dispatch(Options.setPendingBuildOptions(null));
                        }),
                    )();
                }
            }

            if (
                productPending === null ||
                !Fixtuur.Configuration.Selections.equals(productSelections, productPending)
            ) {
                const cached = Cache.get(productSelections, state.options.cache);

                if (O.isSome(cached)) {
                    pipe(
                        cached,
                        O.map(options => {
                            dispatch(Options.setProductOptions(options));
                            dispatch(Options.setPendingProductOptions(null));
                        }),
                    );
                } else {
                    dispatch(Options.setPendingProductOptions(productSelections));

                    pipe(
                        Api.Configuration.Options.fromConfigurationSpace(
                            serviceConfig,
                            currentConfigurator.configurationSpaceId,
                            pipe(productSelections, Fixtuur.Configuration.Selections.toApi),
                        ),
                        TE.mapLeft(error => {
                            dispatch(Options.setPendingProductOptions(null));
                            throw error;
                        }),
                        TE.map(Fixtuur.Configuration.Options.fromApi),
                        TE.map(options =>
                            filterOptionsToConfigurator(options, currentConfigurator),
                        ),
                        TE.map(options => {
                            dispatch(Options.setProductOptions(options));
                            dispatch(Options.setCachedOption({ key: productSelections, options }));
                            dispatch(Options.setPendingProductOptions(null));
                        }),
                    )();
                }
            }
        }
    };
}
